import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV_LINKS } from "../../data/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed left-0 right-0 top-0 z-50 transition-all duration-400 ${
          scrolled ? "border-b border-[#111827]/10 bg-white/90 backdrop-blur-xl" : "bg-transparent"
        }`}
      >
        <nav className={`ad-container flex items-center justify-between transition-all duration-400 ${scrolled ? "h-16" : "h-20"}`}>
          <Link to="/" data-testid="logo-link" className="flex items-center gap-0.5" aria-label="All Data Tech home">
            <span className="text-lg font-extrabold tracking-tight text-[#111827] sm:text-xl">ALL DATA TECH</span>
            <span className="text-xl font-extrabold text-[#1677FF]">.</span>
          </Link>

          <div className="hidden items-center gap-8 lg:flex">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors duration-200 ${
                    isActive ? "text-[#1677FF]" : "text-[#4B5563] hover:text-[#111827]"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>

          <div className="hidden lg:block">
            <Link
              to="/consultation"
              data-testid="nav-book-consultation"
              className="group inline-flex items-center gap-2 rounded-full bg-[#1677FF] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#0f5fe0]"
            >
              Book a Consultation
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </Link>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#111827]/10 bg-white text-[#111827] lg:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>
      </header>

      <div
        data-testid="mobile-menu"
        className={`fixed inset-0 z-40 flex flex-col bg-white transition-all duration-300 lg:hidden ${
          open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <div className="mt-24 flex flex-1 flex-col gap-1 px-6">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `border-b border-[#111827]/8 py-5 text-2xl font-bold tracking-tight transition-colors ${
                  isActive ? "text-[#1677FF]" : "text-[#111827]"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <Link
            to="/consultation"
            data-testid="mobile-book-consultation"
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#1677FF] px-6 py-4 text-base font-semibold text-white"
          >
            Book a Consultation <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </>
  );
}
